/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { brand } from '../../theme/themePrimitives';
import BulkMessagesModal from './modal/bulk_message';
import ComposeBulkEmailComponent from '../forms/email_compose';
import CustomDropdown from '../reusable/dropdown';
import CustomInput from '../reusable/input';
import DeleteleadsModal from './modal/delete_leads';
import { fetchAutomationThunk } from '../../redux/thunks/automation';
import { fetchLeadsThunk } from '../../redux/thunks/lead';
import { formatDateWithTime } from '../../helper';
import { getAllPipelinesThunk } from '../../redux/thunks/pipeline';
import { LeadObject } from '../../params/redux/leads';
import PushToPipelineModal from './modal/select_pipeline_stage';
import RunAutomationModal from './modal/run_automation';
import SMSBulkComposeComponent from '../forms/send_sms';
import { useParams } from 'react-router';
import { AddOutlined, DeleteOutline, DynamicFeedOutlined, Search } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, CircularProgress, InputAdornment, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel, GridSortCellParams } from '@mui/x-data-grid';
import { fetchClientIntegrationsThunk, fetchIntegrationsThunk } from '../../redux/thunks/integration';
import { settingSelectedForm, settingSelectedFormFlag } from '../../redux/slices/form';
import { settingSelectedLead, settingSelectedLeadFlag } from '../../redux/slices/leads';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

interface LeadsRow {
    id: number;
    source: string;
    receivedOn: string;
    timestamp: number;
    email: string;
    number: string;
    form_id: number;
}

const LeadComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const [searched, setSearched] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [runAutomation, setRunAutomation] = useState<boolean>(false);
    const [deleteLead, setDeleteLead] = useState<boolean>(false);
    const [bulkMessage, setBulkMessage] = useState<boolean>(false);
    const [sort, setSort] = useState<boolean>(false);
    const [selectedForm, setSelectedForm] = useState<number | null>(null);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const { leads, bulkMessage: bulk, loaders } = useSelector((state: RootState) => state.lead);
    const { forms } = useSelector((state: RootState) => state.form);

    useEffect(() => {
        const fetchLeads = async () => {
            try {
                if (location.pathname.includes('/lead-management') || location.pathname.includes('/leads')) {
                    await dispatch(fetchLeadsThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
                }
            } catch (e) {
                //
            }
        };
        fetchLeads();
    }, [dispatch, id]);

    // **Filtering and Sorting Leads**
    const filteredLeads: LeadObject[] = leads.filter((lead: LeadObject) => {
        const email = lead.email?.toLowerCase() || '';
        const phone = lead.phone?.toLowerCase() || '';
        const title = lead.source?.toLowerCase() || '';
        return searched
            ? email.includes(searched.toLowerCase()) ||
            phone.includes(searched.toLowerCase()) ||
            title.includes(searched.toLowerCase())
            : true;
    });

    const sortedLeads = sort
        ? [...filteredLeads].sort((a, b) => (a.source && b.source ? a.source.localeCompare(b.source) : 0))
        : filteredLeads;

    const filteredLeadsFromForm = selectedForm
        ? sortedLeads.filter((lead) => lead.form_id === selectedForm)
        : sortedLeads;

    // **Rows Mapping for DataGrid**
    const rows: LeadsRow[] = filteredLeadsFromForm.map((lead: LeadObject) => ({
        id: lead.id,
        source: lead.source,
        receivedOn: lead.created_at,
        timestamp: new Date(lead.created_at).getTime(),
        email: lead.email || '-',
        number: lead.phone || '-',
        form_id: lead.form_id || 0
    })) || [];

    const openLeadHandler = (lead: LeadsRow) => {
        const selectedFormFromForms = forms.find((form) => form.id === lead.form_id);
        const selectedLead = leads.find((l) => l.id === lead.id);
        dispatch(settingSelectedForm(selectedFormFromForms));
        dispatch(settingSelectedFormFlag(true));
        dispatch(settingSelectedLead(selectedLead));
        dispatch(settingSelectedLeadFlag(true));
    };

    // **Columns Definition for DataGrid**
    const columns: GridColDef<LeadsRow>[] = [
        {
            field: 'source',
            headerName: 'SOURCE',
            flex: 1,
            renderCell: (params: GridRenderCellParams<LeadsRow, string>) => (
                <Box marginY="15px" sx={{ cursor: 'pointer' }}>
                    <Typography variant="body1" fontWeight="600" color={brand[300]} onClick={() => openLeadHandler(params.row)}>
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: 'receivedOn',
            headerName: 'RECEIVED ON',
            flex: 1,
            sortComparator: (v1: unknown, v2: unknown, param1: GridSortCellParams<LeadsRow>, param2: GridSortCellParams<LeadsRow>) => {
                const row1 = rows.find((row) => row.id === param1.id);
                const row2 = rows.find((row) => row.id === param2.id);
                return (row1?.timestamp || 0) - (row2?.timestamp || 0);
            },
            renderCell: (params: GridRenderCellParams<LeadsRow, string>) => (
                <Box marginY="14px">
                    <Typography variant="body1">
                        {params.value && formatDateWithTime(params.value)}
                    </Typography>
                </Box>
            )
        },
        { field: 'email', headerName: 'EMAIL', flex: 1 },
        { field: 'number', headerName: 'PHONE NUMBER', flex: 1 },
        {
            field: 'id',
            headerName: 'ACTIONS',
            flex: 1,
            renderCell: () => <Box></Box>
        }
    ];

    const InserLeadsToPipelineHandler = async () => {
        try {
            await dispatch(getAllPipelinesThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
            setShowModal(true);
        } catch (e) {
            //
        }
    };

    const runAutomationHandler = async () => {
        try {
            await dispatch(fetchAutomationThunk(id ? parseInt(id as string, 10) : 0)).unwrap();
            setRunAutomation(true);
        } catch (e) {
            //
        }
    };

    const bulkMessageHandler = async () => {
        try {
            if (id) {
                await dispatch(fetchClientIntegrationsThunk(parseInt(id as string, 10))).unwrap();
            } else {
                await dispatch(fetchIntegrationsThunk()).unwrap();
            }
            setBulkMessage(true);
        } catch (e) {
            //
        }
    };

    if (loaders.isLoading) {
        return <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="60vh" flex={1} gap={2} >
                    <CircularProgress />
                    <Typography variant="subtitle1">Loading...</Typography>
                </Box>;
    }
    if (bulk.type === 'email') {
        return <ComposeBulkEmailComponent />;
    } else if (bulk.type === 'sms') {
        return <SMSBulkComposeComponent />;
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" my={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <CustomInput
                        placeholder="Search..."
                        value={searched}
                        slotProps={{
                                                input: {
                                                    startAdornment: <InputAdornment position="start">
                                                        <Search />
                                                    </InputAdornment>
                                                }
                                            }}
                        onChange={(e) => setSearched(e.target.value)}
                    />
                    <Button
                        variant="outlined"
                        color={sort ? 'primary' : 'secondary'}
                        onClick={() => setSort(!sort)}
                    >
                        {sort ? 'Sorted' : 'Sort'}
                    </Button>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <Box display="none">
                        <Button variant="outlined" color="secondary">
                            Apply Filter
                        </Button>
                    </Box>
                    <CustomDropdown
                        options={[
                            { label: 'All Forms', value: null },
                            ...(forms || []).map((form) => ({
                                label: form.title,
                                value: form.id
                            }))
                        ]}
                        value={selectedForm}
                        onChange={(e) => setSelectedForm(e.target.value ? Number(e.target.value) : null)}
                        sx={{
                            marginBottom: '0'
                        }}
                    />
                </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
                <Typography variant="subtitle2">
                    Total number of leads - {leads.length}
                </Typography>
                {selectedRows.length !== 0 && <Box display="flex" gap={2}>
                    <Button variant="contained" sx={{ display: 'none' }} onClick={runAutomationHandler}>Run Automation</Button>
                    <Button variant="contained" startIcon={<AddOutlined />} onClick={InserLeadsToPipelineHandler}>Push to Pipeline</Button>
                    <Button variant="contained" startIcon={<DynamicFeedOutlined />} onClick={bulkMessageHandler}>Send Bulk Message</Button>
                    <Button variant="outlined" startIcon={<DeleteOutline />} color="error" onClick={() => setDeleteLead(true)}>Delete Selected</Button>
                </Box>}
            </Box>
            <div style={{ height: '62vh', width: '100%' }}>
                <DataGrid
                    rows={rows as LeadsRow[]}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20
                            }
                        }
                    }}
                    rowSelectionModel={selectedRows}
                    pageSizeOptions={[5, 10, 20]}
                    onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => setSelectedRows(newSelection)}
                    checkboxSelection
                    sx={{
                        '& .MuiDataGrid-row--borderBottom': {
                            backgroundColor: `${brand[50]} !important`
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '14px'
                        },
                        'width': '100%',
                        'overflowX': 'hidden'
                    }}
                />
            </div>
            <PushToPipelineModal open={showModal} onClose={() => setShowModal(false)} selectedRows={selectedRows as number[]} />
            <RunAutomationModal open={runAutomation} onClose={() => setRunAutomation(false)} selectedRows={selectedRows as number[]} />
            <BulkMessagesModal open={bulkMessage} onClose={() => setBulkMessage(false)} selectedRows={selectedRows as number[]} />
            <DeleteleadsModal open={deleteLead} onClose={() => setDeleteLead(false)} selectedRows={selectedRows as number[]} />
        </Box>
    );
};

export default LeadComponent;
